import S3 from "aws-s3-pro";
import MIMES from "@/module/commons/mimes.json";
import { has } from "lodash";

const MAX_IMAGE_SIZE_PDF = 200;

export default {
    methods: {
        getFileExtensionFromBase64(base64) {
            const mimeFromBase64 = base64
                .split(";base64")[0]
                .replace("data:", "");
            let extension = mimeFromBase64;
            extension = mimeFromBase64.split("/")[1];

            if (!has(MIMES, extension)) {
                // TODO: Problem here as the mime appeared in multiple keys in MIMES
                // Maybe need to find the library or any sources that has compiled list of extensions based on mimes
                extension = Object.keys(MIMES).find(key => {
                    return (
                        MIMES[key] === mimeFromBase64 ||
                        (Array.isArray(MIMES[key]) &&
                            MIMES[key].includes(mimeFromBase64))
                    );
                });
            }
            return extension;
        },
        async base64ToFile(fileName, base64Data) {
            const base64Response = await fetch(base64Data);
            const blob = await base64Response.blob();
            return new File(
                [blob],
                `${fileName}.${this.getFileExtensionFromBase64(base64Data)}`,
                {
                    type: blob.type
                }
            );
        },
        /**
         * toBase64 : used in webform to render the file choosen before save the webform
         * @param {blob} file
         * @returns based64
         */
        async toBase64(file) {
            return new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.readAsDataURL(file);
                reader.onload = () => resolve(reader.result);
                reader.onerror = error => reject(error);
            });
        },
        /**
         * @method storeS3
         * @description Handle upload multiple files simultaneously to S3
         * @param {*} params
         * array of files
         * params[].file
         * params[].fileName
         * * @param {*} module
         * module.id
         * module.name
         * @returns
         */
        async storeS3(params, module) {
            let self = this;
            let promises = [];

            // for webform page, the path is send from the webform component
            let s3Path = module.path
                ? module.path
                : module.name +
                  "/" +
                  this.$store.state.setting.business_id +
                  "/" +
                  module.id;

            let config = {
                dirName: s3Path,
                bucketName: process.env.S3_BUCKET,
                region: process.env.S3_REGION,
                accessKeyId: process.env.S3_KEY,
                secretAccessKey: process.env.S3_SECRET,
                s3Url: process.env.S3_URI
            };

            const S3Client = new S3(config);

            params.forEach(param => {
                if (!self.isMaxFileSize(param.file)) {
                    return false;
                }
                let parts = self._get_fileparts(param.file.name);
                let fileObj = {
                    title:
                        self._sanitizeFilename(parts[0], true) + "." + parts[1],
                    filename: param.fileName
                        ? param.fileName
                        : self._sanitizeFilename(parts[0], true) +
                          "_" +
                          self.uniqid(),
                    ext: parts[1]
                };
                promises.push(
                    S3Client.uploadFile(param.file, fileObj.filename).then(
                        result => {
                            if (result.status === 204) {
                                // somehow,the s3client return double /, so, convert it to only one / to be able to access
                                // TODO: Should use return process.env.S3_URI + fileObje.path
                                fileObj.path = result.location.replace(
                                    `.com//${module.name}`,
                                    `.com/${module.name}`
                                );
                                return fileObj;
                            } else {
                                return false;
                            }
                        }
                    )
                );
            });
            return Promise.all(promises);
        },
        /**
         * isMaxFileSize
         * Max total size of files upload should not more that 200MB
         * @param {File} file
         * @returns {Boolean}
         */
        isMaxFileSize(file) {
            // based on attachment in patient module
            if (file.size > 200 * 1000 * 1000) {
                this._message(
                    " Total file size exceed limit (200.0 MB).",
                    "danger"
                );
                return false;
            } else {
                return true;
            }
        },
        /**
         * convertFileURLToBase64
         * @param {String} url
         * @returns Promises
         * need to use .then or await
         */
        async convertFileURLToBase64(url) {
            return new Promise((res, rej) => {
                this.conversion(url, response => {
                    res(response);
                });
            });
        },
        /**
         * conversion
         * Use convertFileURLToBase64 instead of this method with await
         * @param {String} url
         * @param {Function} callback
         */
        async conversion(url, callback) {
            if (!this.checkIsURL(url)) {
                callback({ base64: url });
                return;
            }

            try {
                const response = await fetch(url);

                if (!response.ok) {
                    // Handle network errors or non-2xx HTTP responses
                    throw new Error("Load image error!");
                }

                const blob = await response.blob();
                const reader = new FileReader();
                reader.onload = function (e) {
                    var img = new Image();
                    img.onload = () => {
                        // Calculate scale factor to reduce the height and width based on the highest dimension
                        let scaleFactor = 1;
                        if (
                            img.width > MAX_IMAGE_SIZE_PDF ||
                            img.height > MAX_IMAGE_SIZE_PDF
                        ) {
                            if (img.width > img.height) {
                                scaleFactor = MAX_IMAGE_SIZE_PDF / img.width;
                            } else {
                                scaleFactor = MAX_IMAGE_SIZE_PDF / img.height;
                            }

                            const width = img.width * scaleFactor;
                            const height = img.height * scaleFactor;

                            // Use a canvas to resize the image
                            const canvas = document.createElement("canvas");
                            canvas.width = width;
                            canvas.height = height;
                            const ctx = canvas.getContext("2d");
                            ctx.drawImage(img, 0, 0, width, height);

                            callback({
                                base64: canvas.toDataURL(),
                                width: width,
                                height: height
                            });
                        } else {
                            callback({
                                base64: e.target.result,
                                width: img.width,
                                height: img.height
                            });
                        }
                    };
                    img.onerror = () => {
                        console.error(
                            "Could not load the image for dimension measurement."
                        );
                        callback({ base64: e.target.result });
                    };
                    img.src = e.target.result;
                };
                reader.readAsDataURL(blob);
            } catch (error) {
                console.error(error);
                callback({ base64: url });
            }
        },
        checkIsURL(url) {
            try {
                url = new URL(url);
                return url.protocol === "http:" || url.protocol === "https:";
            } catch (e) {
                return false;
            }
        },
        // TODO: WHAT TODO FOR THE FUNCTION IN HELPER.JS THAT NEED TO BE USED HERE
        /**
         * _sanitizeFilename
         * COPIED FUNCTION FROM HELPER
         * @param {*} string
         * @param {*} force_lowercase
         * @returns
         */
        _sanitizeFilename(string, force_lowercase = true) {
            let self = this;
            let strip = [
                "~",
                "`",
                "!",
                "@",
                "#",
                "$",
                "%",
                "^",
                "&",
                "*",
                "(",
                ")",
                "=",
                "+",
                "[",
                "{",
                "]",
                "}",
                "\\",
                "|",
                ";",
                ":",
                '"',
                "'",
                "&#8216;",
                "&#8217;",
                "&#8220;",
                "&#8221;",
                "&#8211;",
                "&#8212;",
                "â€”",
                "â€“",
                ",",
                "<",
                ".",
                ">",
                "/",
                "?",
                "-",
                "#"
            ];
            string = string.split(" ").join("_");
            let clean = self
                ._splitMulti(self._stripTags(string), strip)
                .join("_")
                .trim();
            clean = clean.replace(/\s+/g, "");

            return force_lowercase ? clean.toLowerCase() : clean;
        },
        _stripTags(string) {
            return string.replace(/(<([^>]+)>)/gi, "");
        },
        _splitMulti(str, seperator) {
            var tempChar = seperator[0];
            for (var i = 1; i < seperator.length; i++) {
                str = str.split(seperator[i]).join(tempChar);
            }
            str = str.split(tempChar);
            return str;
        },
        _get_fileparts(str) {
            let self = this;
            let fname = self._get_filename(str);
            let fext = self._get_extension(str);

            return [fname, fext];
        },
        _get_filename(str) {
            let last_index_of = str.lastIndexOf(".");
            let name = str;

            if (last_index_of !== 0) {
                name = str.substr(0, last_index_of);
            }
            return name;
        },
        _get_extension(str) {
            let last_index_of = str.lastIndexOf(".");
            let ext = "";

            if (last_index_of !== 0) {
                ext = str.substr(last_index_of + 1);
            }

            return ext;
        }
    }
};
